import { useState, useEffect, useRef, useMemo, useCallback, type RefObject } from 'react';
import type { TrainerExerciseListRow } from '../../../../types/dataModel';
import type { ExLibSortKey, LibEmptyVariant } from '../types/library';
import { LIB_EX_SORT_OPTIONS, LIB_EX_UNSPECIFIED, VALID_EX_SORT_KEYS } from '../types/library';
import { readExViewState, writeExViewState } from '../utils/libraryViewState';
import { splitTags } from '../../../../utils/exerciseDisplayUtils';

export const LIB_EXERCISES_PAGE_SIZE = 15;

export type UseLibraryExercisesParams = {
  exercises: TrainerExerciseListRow[];
  trainerId: string | undefined;
};

export function useLibraryExercises({ exercises, trainerId }: UseLibraryExercisesParams) {
  const [exerciseNameFilter, setExerciseNameFilter] = useState<string>(() => {
    const saved = readExViewState();
    return typeof saved.search === 'string' ? saved.search : '';
  });
  const [libExerciseMuscleFilter, setLibExerciseMuscleFilter] = useState<Set<string>>(() => {
    const saved = readExViewState();
    return new Set(Array.isArray(saved.muscle) ? saved.muscle.filter((v): v is string => typeof v === 'string') : []);
  });
  const [libExerciseEquipmentFilter, setLibExerciseEquipmentFilter] = useState<Set<string>>(() => {
    const saved = readExViewState();
    return new Set(Array.isArray(saved.equipment) ? saved.equipment.filter((v): v is string => typeof v === 'string') : []);
  });
  const [libExerciseCreatedByFilter, setLibExerciseCreatedByFilter] = useState<Set<'You' | 'Bravos'>>(() => {
    const saved = readExViewState();
    const valid = Array.isArray(saved.createdBy)
      ? saved.createdBy.filter((v): v is 'You' | 'Bravos' => v === 'You' || v === 'Bravos')
      : [];
    return new Set(valid);
  });
  const [libExerciseSort, setLibExerciseSort] = useState<ExLibSortKey>(() => {
    const saved = readExViewState();
    return saved.sort && VALID_EX_SORT_KEYS.includes(saved.sort) ? saved.sort : 'name-asc';
  });
  const [libExerciseSortExplicit, setLibExerciseSortExplicit] = useState(false);
  const [libExercisesPage, setLibExercisesPage] = useState<number>(() => {
    const saved = readExViewState();
    const n = typeof saved.page === 'number' && Number.isFinite(saved.page) && saved.page >= 1
      ? Math.floor(saved.page)
      : 1;
    return n;
  });
  const [libExerciseOpenChip, setLibExerciseOpenChip] = useState<'muscle' | 'equipment' | 'createdBy' | 'sort' | null>(null);
  const libExerciseMuscleChipRef = useRef<HTMLDivElement>(null);
  const libExerciseEquipmentChipRef = useRef<HTMLDivElement>(null);
  const libExerciseCreatedByChipRef = useRef<HTMLDivElement>(null);
  const libExerciseSortChipRef = useRef<HTMLDivElement>(null);

  const libExerciseMuscleOptions = useMemo<{ values: string[]; hasUnspecified: boolean }>(() => {
    const set = new Set<string>();
    let hasUnspecified = false;
    for (const ex of exercises) {
      const tags = splitTags(ex.muscleGroup);
      if (tags.length === 0) hasUnspecified = true;
      else tags.forEach((t) => set.add(t));
    }
    return { values: Array.from(set).sort((a, b) => a.localeCompare(b)), hasUnspecified };
  }, [exercises]);

  const libExerciseEquipmentOptions = useMemo<{ values: string[]; hasUnspecified: boolean }>(() => {
    const set = new Set<string>();
    let hasUnspecified = false;
    for (const ex of exercises) {
      const tags = splitTags(ex.equipment);
      if (tags.length === 0) hasUnspecified = true;
      else tags.forEach((t) => set.add(t));
    }
    return { values: Array.from(set).sort((a, b) => a.localeCompare(b)), hasUnspecified };
  }, [exercises]);

  const filteredAndSortedExercises = useMemo<TrainerExerciseListRow[]>(() => {
    const query = exerciseNameFilter.trim().toLowerCase();

    const filtered = exercises.filter((ex) => {
      if (query && !ex.name.toLowerCase().includes(query)) return false;

      if (libExerciseMuscleFilter.size > 0) {
        const muscleTags = splitTags(ex.muscleGroup);
        const muscleMatch =
          (libExerciseMuscleFilter.has(LIB_EX_UNSPECIFIED) && muscleTags.length === 0)
          || muscleTags.some((t) => libExerciseMuscleFilter.has(t));
        if (!muscleMatch) return false;
      }

      if (libExerciseEquipmentFilter.size > 0) {
        const equipmentTags = splitTags(ex.equipment);
        const equipmentMatch =
          (libExerciseEquipmentFilter.has(LIB_EX_UNSPECIFIED) && equipmentTags.length === 0)
          || equipmentTags.some((t) => libExerciseEquipmentFilter.has(t));
        if (!equipmentMatch) return false;
      }

      if (libExerciseCreatedByFilter.size > 0) {
        const isYou = ex.createdBy === trainerId;
        const wantYou = libExerciseCreatedByFilter.has('You');
        const wantBravos = libExerciseCreatedByFilter.has('Bravos');
        if (isYou && !wantYou) return false;
        if (!isYou && !wantBravos) return false;
      }
      return true;
    });

    const tieByName = (a: TrainerExerciseListRow, b: TrainerExerciseListRow) =>
      a.name.localeCompare(b.name);
    const sorted = [...filtered];
    switch (libExerciseSort) {
      case 'name-asc':
        sorted.sort(tieByName);
        break;
      case 'name-desc':
        sorted.sort((a, b) => -tieByName(a, b));
        break;
      case 'yours-first':
        sorted.sort((a, b) => {
          const ay = a.createdBy === trainerId ? 0 : 1;
          const by = b.createdBy === trainerId ? 0 : 1;
          if (ay !== by) return ay - by;
          return tieByName(a, b);
        });
        break;
      case 'yours-last':
        sorted.sort((a, b) => {
          const ay = a.createdBy === trainerId ? 1 : 0;
          const by = b.createdBy === trainerId ? 1 : 0;
          if (ay !== by) return ay - by;
          return tieByName(a, b);
        });
        break;
    }
    return sorted;
  }, [
    exercises,
    exerciseNameFilter,
    libExerciseMuscleFilter,
    libExerciseEquipmentFilter,
    libExerciseCreatedByFilter,
    libExerciseSort,
    trainerId,
  ]);

  /** Mobile list uses name search only (legacy mobile path). */
  const filteredExercisesByName = useMemo(() => {
    const trimmed = exerciseNameFilter.trim().toLowerCase();
    if (!trimmed) return exercises;
    return exercises.filter((ex) => ex.name.toLowerCase().includes(trimmed));
  }, [exercises, exerciseNameFilter]);

  const exercisePassesWithout = useCallback(
    (ex: TrainerExerciseListRow, exclude: 'muscle' | 'equipment' | 'createdBy'): boolean => {
      const query = exerciseNameFilter.trim().toLowerCase();
      if (query && !ex.name.toLowerCase().includes(query)) return false;
      if (exclude !== 'muscle' && libExerciseMuscleFilter.size > 0) {
        const muscleTags = splitTags(ex.muscleGroup);
        const muscleMatch =
          (libExerciseMuscleFilter.has(LIB_EX_UNSPECIFIED) && muscleTags.length === 0)
          || muscleTags.some((t) => libExerciseMuscleFilter.has(t));
        if (!muscleMatch) return false;
      }
      if (exclude !== 'equipment' && libExerciseEquipmentFilter.size > 0) {
        const equipmentTags = splitTags(ex.equipment);
        const equipmentMatch =
          (libExerciseEquipmentFilter.has(LIB_EX_UNSPECIFIED) && equipmentTags.length === 0)
          || equipmentTags.some((t) => libExerciseEquipmentFilter.has(t));
        if (!equipmentMatch) return false;
      }
      if (exclude !== 'createdBy' && libExerciseCreatedByFilter.size > 0) {
        const isYou = ex.createdBy === trainerId;
        const wantYou = libExerciseCreatedByFilter.has('You');
        const wantBravos = libExerciseCreatedByFilter.has('Bravos');
        if (isYou && !wantYou) return false;
        if (!isYou && !wantBravos) return false;
      }
      return true;
    },
    [
      exerciseNameFilter,
      libExerciseMuscleFilter,
      libExerciseEquipmentFilter,
      libExerciseCreatedByFilter,
      trainerId,
    ],
  );

  const libExerciseMuscleMatchCounts = useMemo(() => {
    const counts = new Map<string, number>();
    libExerciseMuscleOptions.values.forEach((v) => counts.set(v, 0));
    let unspecified = 0;
    for (const ex of exercises) {
      if (!exercisePassesWithout(ex, 'muscle')) continue;
      const tags = splitTags(ex.muscleGroup);
      if (tags.length === 0) unspecified++;
      else {
        for (const t of tags) {
          if (counts.has(t)) counts.set(t, (counts.get(t) ?? 0) + 1);
        }
      }
    }
    return { counts, unspecified };
  }, [exercises, libExerciseMuscleOptions, exercisePassesWithout]);

  const libExerciseEquipmentMatchCounts = useMemo(() => {
    const counts = new Map<string, number>();
    libExerciseEquipmentOptions.values.forEach((v) => counts.set(v, 0));
    let unspecified = 0;
    for (const ex of exercises) {
      if (!exercisePassesWithout(ex, 'equipment')) continue;
      const tags = splitTags(ex.equipment);
      if (tags.length === 0) unspecified++;
      else {
        for (const t of tags) {
          if (counts.has(t)) counts.set(t, (counts.get(t) ?? 0) + 1);
        }
      }
    }
    return { counts, unspecified };
  }, [exercises, libExerciseEquipmentOptions, exercisePassesWithout]);

  const libExerciseCreatedByMatchCounts = useMemo<{ You: number; Bravos: number }>(() => {
    const out = { You: 0, Bravos: 0 };
    for (const ex of exercises) {
      if (!exercisePassesWithout(ex, 'createdBy')) continue;
      if (ex.createdBy === trainerId) out.You++;
      else out.Bravos++;
    }
    return out;
  }, [exercises, exercisePassesWithout, trainerId]);

  const libExercisesHasActiveControls =
    libExerciseMuscleFilter.size > 0
    || libExerciseEquipmentFilter.size > 0
    || libExerciseCreatedByFilter.size > 0
    || exerciseNameFilter.trim() !== ''
    || libExerciseSortExplicit
    || libExerciseSort !== 'name-asc';

  const libExercisesAppliedCount =
    libExerciseMuscleFilter.size
    + libExerciseEquipmentFilter.size
    + libExerciseCreatedByFilter.size
    + (exerciseNameFilter.trim() !== '' ? 1 : 0)
    + ((libExerciseSortExplicit || libExerciseSort !== 'name-asc') ? 1 : 0);

  const handleClearAllExercises = useCallback(() => {
    setLibExerciseMuscleFilter(new Set());
    setLibExerciseEquipmentFilter(new Set());
    setLibExerciseCreatedByFilter(new Set());
    setExerciseNameFilter('');
    setLibExerciseSort('name-asc');
    setLibExerciseSortExplicit(false);
    setLibExercisesPage(1);
  }, []);

  const libExercisesTotalPages = Math.max(
    1,
    Math.ceil(filteredAndSortedExercises.length / LIB_EXERCISES_PAGE_SIZE),
  );

  const libExercisesPageRows = useMemo<TrainerExerciseListRow[]>(() => {
    const start = (libExercisesPage - 1) * LIB_EXERCISES_PAGE_SIZE;
    return filteredAndSortedExercises.slice(start, start + LIB_EXERCISES_PAGE_SIZE);
  }, [filteredAndSortedExercises, libExercisesPage]);

  const libExerciseRangeStart = filteredAndSortedExercises.length === 0
    ? 0
    : (libExercisesPage - 1) * LIB_EXERCISES_PAGE_SIZE + 1;
  const libExerciseRangeEnd = Math.min(
    filteredAndSortedExercises.length,
    libExercisesPage * LIB_EXERCISES_PAGE_SIZE,
  );

  const libExFiltersHydratedRef = useRef(false);
  useEffect(() => {
    if (!libExFiltersHydratedRef.current) {
      libExFiltersHydratedRef.current = true;
      return;
    }
    setLibExercisesPage(1);
  }, [
    exerciseNameFilter,
    libExerciseMuscleFilter,
    libExerciseEquipmentFilter,
    libExerciseCreatedByFilter,
    libExerciseSort,
    exercises.length,
  ]);

  useEffect(() => {
    writeExViewState({
      search: exerciseNameFilter,
      muscle: Array.from(libExerciseMuscleFilter),
      equipment: Array.from(libExerciseEquipmentFilter),
      createdBy: Array.from(libExerciseCreatedByFilter),
      sort: libExerciseSort,
      page: libExercisesPage,
    });
  }, [
    exerciseNameFilter,
    libExerciseMuscleFilter,
    libExerciseEquipmentFilter,
    libExerciseCreatedByFilter,
    libExerciseSort,
    libExercisesPage,
  ]);

  useEffect(() => {
    if (libExercisesPage > libExercisesTotalPages) setLibExercisesPage(libExercisesTotalPages);
  }, [libExercisesPage, libExercisesTotalPages]);

  useEffect(() => {
    if (!libExerciseOpenChip) return;
    const handle = (e: Event) => {
      const target = e.target as Node;
      const refs: [typeof libExerciseOpenChip, RefObject<HTMLDivElement | null>][] = [
        ['muscle', libExerciseMuscleChipRef],
        ['equipment', libExerciseEquipmentChipRef],
        ['createdBy', libExerciseCreatedByChipRef],
        ['sort', libExerciseSortChipRef],
      ];
      for (const [name, ref] of refs) {
        if (libExerciseOpenChip === name && ref.current && !ref.current.contains(target)) {
          setLibExerciseOpenChip(null);
        }
      }
    };
    document.addEventListener('mousedown', handle);
    document.addEventListener('touchstart', handle);
    return () => {
      document.removeEventListener('mousedown', handle);
      document.removeEventListener('touchstart', handle);
    };
  }, [libExerciseOpenChip]);

  const libExerciseSortIsDefault = libExerciseSort === 'name-asc';
  const libExerciseSortShowsSelection = libExerciseSortExplicit || !libExerciseSortIsDefault;
  const libExerciseCurrentSortLabel =
    LIB_EX_SORT_OPTIONS.find((opt) => opt.id === libExerciseSort)?.label ?? 'Name (A→Z)';

  const handleLibExerciseHeaderSort = useCallback((column: 'name' | 'createdBy') => {
    setLibExerciseSortExplicit(true);
    if (column === 'name') {
      setLibExerciseSort((curr) => (curr === 'name-asc' ? 'name-desc' : 'name-asc'));
    } else {
      setLibExerciseSort((curr) => (curr === 'yours-first' ? 'yours-last' : 'yours-first'));
    }
  }, []);

  const libExercisesEmptyVariant: LibEmptyVariant = (() => {
    const youOnly =
      libExerciseCreatedByFilter.has('You')
      && !libExerciseCreatedByFilter.has('Bravos');
    if (filteredAndSortedExercises.length === 0 && youOnly) {
      return 'filtered-empty-exercises';
    }
    if (filteredAndSortedExercises.length === 0 && libExercisesHasActiveControls) {
      return 'no-match';
    }
    return null;
  })();

  const libBuildPageWindow = useCallback((current: number, total: number): Array<number | 'ellipsis'> => {
    if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
    if (current <= 3) return [1, 2, 3, 4, 'ellipsis', total];
    if (current >= total - 2) return [1, 'ellipsis', total - 3, total - 2, total - 1, total];
    return [1, 'ellipsis', current - 1, current, current + 1, 'ellipsis', total];
  }, []);

  return {
    exerciseNameFilter,
    setExerciseNameFilter,
    libExerciseMuscleFilter,
    setLibExerciseMuscleFilter,
    libExerciseEquipmentFilter,
    setLibExerciseEquipmentFilter,
    libExerciseCreatedByFilter,
    setLibExerciseCreatedByFilter,
    libExerciseSort,
    setLibExerciseSort,
    libExerciseSortExplicit,
    setLibExerciseSortExplicit,
    libExercisesPage,
    setLibExercisesPage,
    libExerciseOpenChip,
    setLibExerciseOpenChip,
    libExerciseMuscleChipRef,
    libExerciseEquipmentChipRef,
    libExerciseCreatedByChipRef,
    libExerciseSortChipRef,
    libExerciseMuscleOptions,
    libExerciseEquipmentOptions,
    filteredAndSortedExercises,
    filteredExercisesByName,
    libExerciseMuscleMatchCounts,
    libExerciseEquipmentMatchCounts,
    libExerciseCreatedByMatchCounts,
    libExercisesHasActiveControls,
    libExercisesAppliedCount,
    handleClearAllExercises,
    libExercisesTotalPages,
    libExercisesPageRows,
    libExerciseRangeStart,
    libExerciseRangeEnd,
    libExerciseSortShowsSelection,
    libExerciseCurrentSortLabel,
    handleLibExerciseHeaderSort,
    libExercisesEmptyVariant,
    libBuildPageWindow,
    LIB_EX_UNSPECIFIED,
  };
}

export type LibraryExercisesApi = ReturnType<typeof useLibraryExercises>;
